import { Close as CloseIcon } from "@mui/icons-material";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useCurrentProfile } from "../../../../../ContextWrapper";
import { bulkLinkUsers } from "../../../../../api/customers";
import { getAllowedAccountRoles, mapRoleLabelToBackend } from "../../../../../utils/userRoleUtils";

export default function UsersBulkEditDialog({ 
  open, 
  onClose, 
  selectedUsers = [],
  onSubmit, 
  onShowSuccess 
}) {
  const { profile } = useCurrentProfile();
  const isAdmin = profile?.type?.toLowerCase() === "admin";

  const [formData, setFormData] = useState({
    account: null,
    role: "",
  });

  // State for account search
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  // State for password reset
  const [passwordReset, setPasswordReset] = useState(false);

  // State for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Extract user types and categories from all selected users
  const userTypes = [...new Set(selectedUsers.map(u => u.userType))];
  const userCategories = [...new Set(selectedUsers.map(u => u.createdByType))];
  
  // Check if all users are external and have same user type
  const allExternal = userCategories.length === 1 && userCategories[0] === 'external';
  const sameUserType = userTypes.length === 1;
  const canBulkEdit = allExternal && sameUserType;
  
  // Extract user type and category from first selected user
  const userType = selectedUsers.length > 0 ? selectedUsers[0].userType : null;
  const userCategory = selectedUsers.length > 0 ? selectedUsers[0].createdByType : null;
  const isInternalUser = userCategory === 'internal';

  // Get allowed roles based on user type
  const allowedRoles = userType ? getAllowedAccountRoles(userType) : [];

  const searchAccounts = useCallback(async (searchTerm = "") => {
    setLoadingAccounts(true);
    try {
      const params = { page_size: 50 };
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await axios.get("/api/v2/customers/", {
        headers: { "Content-Type": "application/json" },
        params,
        withCredentials: true,
      });
      
      const accountOptions = (response.data.results || []).map((customer) => ({
        id: customer.id,
        name: customer.name,
        label: customer.name,
        email: customer.email,
      }));
      
      setAccounts(accountOptions);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  // Prefetch initial account list when dialog opens
  useEffect(() => {
    if (open) {
      searchAccounts();
    }
  }, [open, searchAccounts]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);

    try {
      const profileIds = selectedUsers.map(u => u.id);
      let passwordResetSuccess = false;
      let accountLinkSuccess = false;
      const successMessages = [];
      
      // Handle password reset if enabled
      if (passwordReset) {
        try {
          const response = await axios.post(
            "/api/v2/bulk_password_reset/",
            { profile_ids: profileIds },
            {
              headers: { "Content-Type": "application/json" },
              withCredentials: true,
            }
          );
          
          if (response.data.success) {
            passwordResetSuccess = true;
            successMessages.push(`Password reset emails sent to ${response.data.count} user(s)`);
          }
        } catch (error) {
          console.error("Bulk password reset error:", error);
          if (onShowSuccess) {
            const errorMessage = error.response?.data?.error || "Failed to send password reset emails. Please try again.";
            onShowSuccess(errorMessage, "error");
          }
          return;
        }
      }

      // Handle bulk linking to account if account and role are selected
      if (canBulkEdit && formData.account && formData.role) {
        try {
          const accountId = formData.account.id;
          const backendRole = mapRoleLabelToBackend(formData.role);
          
          // Build profile_roles mapping: {profile_id: role}
          // Backend expects string keys, so convert profile IDs to strings
          const profileRoles = {};
          profileIds.forEach(profileId => {
            profileRoles[String(profileId)] = backendRole;
          });

          // Link all selected users to the account with the selected role
          // This is atomic - if any user fails, the entire operation fails
          const response = await bulkLinkUsers(accountId, profileIds, [], profileRoles);
          
          // Check if the response indicates any errors (backend should return error on failure)
          if (response.data?.error) {
            throw new Error(response.data.error);
          }
          
          accountLinkSuccess = true;
          successMessages.push(`${selectedUsers.length} user(s) linked to account`);
        } catch (error) {
          console.error("Bulk link users error:", error);
          if (onShowSuccess) {
            const errorMessage = error.response?.data?.error || error.message || "Failed to link users to account. The operation was rolled back - no users were linked.";
            onShowSuccess(errorMessage, "error");
          }
          return;
        }
      }

      // Show success message if any operation succeeded
      if (passwordResetSuccess || accountLinkSuccess) {
        if (onShowSuccess) {
          const message = successMessages.join(" and ") + " successfully!";
          onShowSuccess(message, "success", passwordResetSuccess ? 5000 : undefined);
        }
        if (onSubmit) {
          onSubmit();
        }
        onClose();
        return;
      }

      // If we reach here, no operations were performed
      // This shouldn't happen if form validation is working correctly
      if (onShowSuccess) {
        onShowSuccess("No changes to apply.", "info");
      }
      onClose();
    } catch (error) {
      console.error("Bulk update error:", error);
      
      if (onShowSuccess) {
        const errorMessage = error.response?.data?.error || "Failed to update users. Please try again.";
        onShowSuccess(errorMessage, "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [canBulkEdit, formData, selectedUsers, passwordReset, onSubmit, onShowSuccess, onClose]);

  // Check if form is valid
  const isFormValid = () => {
    // If password reset is enabled, allow submission (admin only)
    if (passwordReset && isAdmin) {
      return true;
    }
    // If can bulk edit, require both account and role
    if (canBulkEdit) {
      return formData.account !== null && formData.role;
    }
    // Otherwise, form is not valid
    return false;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
      sx={{
        zIndex: 99999,
        '& .MuiAutocomplete-popper': {
          zIndex: 999999,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Bulk Edit Users ({selectedUsers.length})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Assign selected users to an account with a specific role.
            </Typography>
          </Box>
          <CloseIcon 
            onClick={onClose}
            sx={{ 
              cursor: 'pointer', 
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' }
            }} 
          />
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={2}>
          {/* Primary Fields Section */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Primary Fields
            </Typography>

            {!canBulkEdit && (
              <Typography 
                variant="body2" 
                color="error" 
                sx={{ mb: 2, fontWeight: 500, fontSize: '0.875rem' }}
              >
                ⚠️ Bulk edit is only available for external users of the same type. Account assignment is disabled. Password reset is still available.
              </Typography>
            )}

            <Grid container rowSpacing={3} columnSpacing={2}>
              {/* User Type Dropdown - For selecting role on account */}
              <Grid size={6}>
                <FormControl 
                  fullWidth 
                  variant="outlined" 
                  size="small"
                  disabled={!canBulkEdit}
                >
                  <InputLabel id="user-type-label">User Type</InputLabel>
                  <Select
                    labelId="user-type-label"
                    id="user-type-select"
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                    label="User Type"
                    disabled={!canBulkEdit}
                    MenuProps={{
                      disablePortal: true,
                      PaperProps: {
                        style: {
                          zIndex: 99999,
                        },
                      },
                    }}
                  >
                    {allowedRoles.map((role) => (
                      <MenuItem key={role} value={role}>{role}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Account Access - Searchable Dropdown */}
              <Grid size={6}>
                <Autocomplete
                  disabled={!canBulkEdit}
                  options={accounts}
                  value={formData.account}
                  loading={loadingAccounts}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.name || '';
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.id === value.id;
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input') {
                      searchAccounts(newInputValue || "");
                    } else if (reason === 'clear') {
                      setFormData(prev => ({ ...prev, account: null }));
                      searchAccounts("");
                    }
                  }}
                  onChange={(event, newValue, reason) => {
                    if (newValue && typeof newValue === 'object') {
                      setFormData(prev => ({ 
                        ...prev, 
                        account: newValue
                      }));
                    } else if (newValue === null || newValue === '') {
                      setFormData(prev => ({ ...prev, account: null }));
                    }
                  }}
                  filterOptions={(options) => options}
                  open={accountOpen}
                  onOpen={() => {
                    setAccountOpen(true);
                    if (accounts.length === 0 && !loadingAccounts) {
                      searchAccounts("");
                    }
                  }}
                  onClose={() => {
                    setAccountOpen(false);
                  }}
                  selectOnFocus
                  clearOnBlur={false}
                  handleHomeEndKeys
                  loadingText="Loading accounts..."
                  noOptionsText="Type to search accounts..."
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Account Access"
                      size="small"
                      fullWidth
                      variant="outlined"
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    const uniqueKey = `account-${option.id}`;
                    return (
                      <li key={uniqueKey} {...otherProps}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>
                            {option.name}
                          </div>
                          {option.email && (
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>
                              {option.email}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  }}
                  slotProps={{
                    popper: {
                      placement: 'bottom-start',
                      style: {
                        zIndex: 999999,
                        position: 'fixed'
                      },
                      modifiers: [
                        {
                          name: 'flip',
                          enabled: false,
                        },
                        {
                          name: 'preventOverflow',
                          enabled: false,
                        },
                      ],
                    },
                  }}
                  ListboxProps={{
                    style: {
                      maxHeight: '200px',
                      overflow: 'auto'
                    }
                  }}
                />

                {/* Warning for internal users */}
                {isInternalUser && formData.account && (
                  <Typography 
                    variant="body2" 
                    color="error" 
                    sx={{ mt: 1, fontWeight: 500, fontSize: '0.875rem' }}
                  >
                    ⚠️ Overriding existing {userType} assignments
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* Password Reset Section */}
          {isAdmin && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Password Reset
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={passwordReset}
                    onChange={(e) => setPasswordReset(e.target.checked)}
                  />
                }
                label="Request Password Change"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    color: '#111827'
                  }
                }}
              />
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ display: 'block', mt: 1 }}
              >
                When enabled, a password reset email will be sent to all selected users upon saving
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{
            color: "#627CFB",
            borderColor: "#627CFB",
            "&:hover": {
              borderColor: "#4f46e5",
              backgroundColor: "rgba(98, 124, 251, 0.04)"
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid() || isSubmitting}
          sx={{
            backgroundColor: "#627CFB",
            color: "white",
            "&:hover": {
              backgroundColor: "#4f46e5"
            }
          }}
        >
          {isSubmitting ? "Updating..." : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

